import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { Dimmer, Loader, Message } from 'semantic-ui-react';

import IneligiblePurchasesList from '../views/IneligiblePurchases/List';
import Search from '../views/Shared/Search';
import Pagination from '../views/Shared/Pagination';
import ineligiblePurchasesQuery from '../graphql/ineligiblePurchasesQuery';
import useGraphQLQuery from '../hooks/useGraphQLQuery';
import { setSearch } from '../data/searchField/actions';
import { DEFAULT_PAGE_SIZE } from '../constants';
import { useGuaranteeProduct } from '../hooks/useGuaranteePurchases';
import { allowAccess } from '../utilities/user';
import ACCESS from '../constants/access';
import APP_PATH from '../constants/paths';
import {
  ROW_ACTION_FORCE_GUARANTEE,
  SELLER_GUARANTEE_PRODUCT_CODE,
} from '../constants/forceGuarantee';
import { buildForceGuaranteeSearch } from '../utilities/forceGuarantee';

const FIRST_PAGE = 1;

const IneligiblePurchases = props => {
  const {
    searchFields, setSearchFields: setSearchFieldsAction, user, history,
  } = props;

  // The menu entry is not rendered without the permission; the route and the backend guard it too.
  const canForceGuarantee = allowAccess(user, ACCESS.FORCE_GUARANTEE);

  const [selectedProduct] = useGuaranteeProduct();
  const [queryState, setQueryState] = useState({
    page: FIRST_PAGE,
    vin: null,
    dealer: null,
  });
  const [productContext, setProductContext] = useState(selectedProduct);
  if (productContext !== selectedProduct) {
    setProductContext(selectedProduct);
    setQueryState(state => ({ ...state, page: FIRST_PAGE }));
  }

  const { page: currentPage, vin, dealer } = queryState;

  const { data, loading, error } = useGraphQLQuery({
    query: ineligiblePurchasesQuery,
    variables: {
      page: currentPage,
      limit: DEFAULT_PAGE_SIZE,
      categoryCode: selectedProduct || null,
      vin,
      dealer,
    },
  });

  const onSearchSubmitHandler = useCallback(
    event => {
      const { vin: vinField, dealerNumbers } = event.target;
      const vinValue = ((vinField && vinField.value) || '').trim() || null;
      const dealerValue = ((dealerNumbers && dealerNumbers.value) || '').trim() || null;
      setQueryState({ page: FIRST_PAGE, vin: vinValue, dealer: dealerValue });
      setSearchFieldsAction({
        vin: vinValue,
        dealerNumbers: dealerValue ? [dealerValue] : null,
      });
    },
    [setSearchFieldsAction]
  );

  const externalIneligiblePurchases = data && data.externalIneligiblePurchases;
  const items = (externalIneligiblePurchases && externalIneligiblePurchases.items) || [];
  const total = (externalIneligiblePurchases && externalIneligiblePurchases.total) || 0;
  const start = items.length > 0 ? (currentPage - 1) * DEFAULT_PAGE_SIZE + 1 : 0;
  const end = start > 0 ? start + items.length - 1 : 0;
  const hasNext = end < total;
  const isLastPage = !hasNext && items.length !== 0;
  // Full-page overlay only until the first items arrive; pagination uses the inline loader.
  const isInitialLoading = loading && items.length === 0;
  const isPaginationLoading = loading && !isInitialLoading;

  const nextHandler = useCallback(() => {
    if (hasNext) setQueryState(state => ({ ...state, page: state.page + 1 }));
  }, [hasNext]);

  const previousHandler = useCallback(() => {
    setQueryState(state => ({
      ...state,
      page: Math.max(FIRST_PAGE, state.page - 1),
    }));
  }, []);

  // Hands the row's context to the Force Guarantee flow through its address, carrying `externalId`.
  const onSelectGuarantee = useCallback(
    (action, ineligiblePurchase) => {
      if (action !== ROW_ACTION_FORCE_GUARANTEE) return;
      const { vin: rowVin, externalId } = ineligiblePurchase || {};
      history.push(
        APP_PATH.FORCE_GUARANTEE
          + buildForceGuaranteeSearch({
            productCode: SELLER_GUARANTEE_PRODUCT_CODE,
            vin: rowVin,
            externalId,
          })
      );
    },
    [history]
  );

  return (
    <div>
      {error && (
        <Message
          negative
          content={error.message || 'An error occurred while loading data.'}
        />
      )}
      <Search
        onSubmit={onSearchSubmitHandler}
        searchFields={searchFields}
        searchOptions={['vin', 'dealerNumbers']}
        multipleSearches
      />
      <Dimmer.Dimmable dimmed={isInitialLoading}>
        <Dimmer active={isInitialLoading} inverted>
          <Loader data-testid='loading-overlay'>Loading...</Loader>
        </Dimmer>

        <IneligiblePurchasesList
          onSelectGuarantee={onSelectGuarantee}
          items={items}
          selectedProduct={selectedProduct}
          canForceGuarantee={canForceGuarantee}
        />

        <Pagination
          start={start}
          end={end}
          nextHandler={nextHandler}
          previousHandler={previousHandler}
          loading={isPaginationLoading}
          lastPage={isLastPage}
          pageSize={DEFAULT_PAGE_SIZE}
        />
      </Dimmer.Dimmable>
    </div>
  );
};

IneligiblePurchases.propTypes = {
  searchFields: PropTypes.shape({
    vin: PropTypes.string,
    dealerNumbers: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  setSearchFields: PropTypes.func.isRequired,
  user: PropTypes.shape({
    roles: PropTypes.arrayOf(PropTypes.string),
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

IneligiblePurchases.defaultProps = {
  user: null,
};

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
});

const mapStateToProps = state => {
  const {
    data: { searchFields, user },
  } = state;
  return { searchFields, user };
};

// Rendered inside GuaranteePurchasesTabs rather than by a Route, so `history` comes from withRouter.
export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(IneligiblePurchases);
